import React from 'react';
import { useTranslation } from 'react-i18next';

import { MetricCard, MetricGrid } from '@/nova/components';
import { AFFILIATE_METRIC_COLORS } from '@/nova/theme/tokens';
import {
  dailyPointLabels,
  dailySeries,
  deltaPct,
  displayMetric,
  formatCountValue,
  formatDeltaPct,
  metricValue,
  type PerfMetricKey,
} from '../../reports-helpers';
import type { AffiliateCommissionsReport, AffiliateMetrics, AffiliatePerformanceRow } from '../../types';
import { Sparkline } from './Sparkline';

export interface KpiRowProps {
  totals?: AffiliateMetrics;
  prevTotals?: AffiliateMetrics;
  commissions?: AffiliateCommissionsReport;
  prevCommissions?: AffiliateCommissionsReport;
  dailyRows: AffiliatePerformanceRow[];
  currency: string;
  loading: boolean;
}

type DeltaProps = { change?: string; changeType?: 'increase' | 'decrease'; vs?: string };

const deltaProps = (curr: number | undefined, prev: number | undefined, vs: string): DeltaProps => {
  const d = deltaPct(curr, prev);
  return d ? { change: formatDeltaPct(d.pct), changeType: d.dir, vs } : {};
};

/**
 * The KPI tile row. Registrations/FTDs/Deposits/GGR/NGR are range-scoped (from the
 * performance totals, with a daily sparkline and a delta vs the previous range); CPAs
 * comes from the monthly commissions report (qualified count), so it deltas vs last
 * month and carries no daily sparkline.
 */
export const KpiRow = ({
  totals,
  prevTotals,
  commissions,
  prevCommissions,
  dailyRows,
  currency,
  loading,
}: KpiRowProps): React.ReactElement => {
  const { t } = useTranslation();
  const vsPeriod = t('vs prev period');
  const dayLabels = dailyPointLabels(dailyRows);

  const perfTiles: Array<{ key: PerfMetricKey; label: string }> = [
    { key: 'registrations', label: t('Registrations') },
    { key: 'ftds', label: t('FTDs') },
  ];
  const perfTilesTail: Array<{ key: PerfMetricKey; label: string }> = [
    { key: 'deposits_usd', label: t('Deposits') },
    { key: 'ggr_usd', label: t('GGR') },
    { key: 'ngr_usd', label: t('NGR') },
  ];

  const perfCard = ({ key, label }: { key: PerfMetricKey; label: string }): React.ReactElement => {
    const value = totals ? displayMetric(metricValue(totals, key), key, currency) : '–';
    const delta = deltaProps(totals ? metricValue(totals, key) : undefined, prevTotals ? metricValue(prevTotals, key) : undefined, vsPeriod);
    return (
      <MetricCard
        key={key}
        label={label}
        value={value}
        surface="panel"
        loading={loading}
        {...delta}
        note={<Sparkline points={dailySeries(dailyRows, key)} color={AFFILIATE_METRIC_COLORS[key]} name={label} pointLabels={dayLabels} />}
      />
    );
  };

  const cpaCurr = commissions?.totals?.cpa_qualified;
  const cpaPrev = prevCommissions?.totals?.cpa_qualified;

  return (
    <MetricGrid>
      {perfTiles.map(perfCard)}
      <MetricCard
        label={t('CPAs')}
        value={commissions ? formatCountValue(cpaCurr ?? 0) : '–'}
        surface="panel"
        loading={loading}
        {...deltaProps(cpaCurr, cpaPrev, t('vs last month'))}
      />
      {perfTilesTail.map(perfCard)}
    </MetricGrid>
  );
};

export default KpiRow;
